import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { ChevronLeft, ChevronRight, ShoppingCart, Clock, Package2 } from 'lucide-react';
import { gsap } from 'gsap';
import { Helmet } from 'react-helmet';
import './ProductPage.css';

// Helper function to optimize Cloudinary URLs
const optimizeImageUrl = (url) => {
  if (url && url.includes('cloudinary')) {
    return url.replace('/upload/', '/upload/q_auto,f_auto,w_800/');
  }
  return url;
};

const SECTION_LAYOUTS = {
  ILE: 'image_left',
  IRG: 'image_right',
  TOP: 'image_top',
  BTM: 'text_top',
  TXT: 'text_only',
  IMG: 'image_only'
};

export default function ProductPage() {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState({});
  const productRef = useRef(null);
  const galleryRef = useRef(null);
  const productCacheRef = useRef({});
  const lastFetchRef = useRef(0);
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const CACHE_DURATION = 300000;

  useEffect(() => {
    if (!slug) return;

    let isMounted = true;
    setLoading(true);
    setImagesLoaded({});
    setCurrentImageIndex(0);

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchProduct = async () => {
      try {
        const now = Date.now();
        const cachedProduct = productCacheRef.current[slug];
        
        if (cachedProduct && now - cachedProduct.timestamp < CACHE_DURATION) {
          console.log('Using cached product data for:', slug);
          setProduct(cachedProduct.data);
          setLoading(false);
          
          if (cachedProduct.data.imageUrls && cachedProduct.data.imageUrls.length > 0) {
            const img = new Image();
            img.src = cachedProduct.data.imageUrls[0];
            img.onload = () => {
              if (isMounted) {
                setImagesLoaded(prev => ({ ...prev, 0: true }));
              }
            };
          }
          return;
        }
        
        if (now - lastFetchRef.current < 1000) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        lastFetchRef.current = Date.now();

        console.log('Fetching product data for:', slug);
        const response = await fetch(`${API_BASE_URL}/api/products/${slug}`, { 
          signal,
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          console.error('Product fetch error:', {
            status: response.status,
            statusText: response.statusText
          });

          // Try to get error details if available
          let errorDetails = 'Unknown error';
          try {
            const errorData = await response.json();
            errorDetails = errorData.message || errorData.error || errorDetails;
          } catch (e) {
            console.error('Error parsing error response:', e);
          }

          if (response.status === 404) {
            setError('not_found');
          } else if (response.status === 429) {
            console.log('Rate limited, retrying after delay');
            setTimeout(() => {
              if (isMounted) fetchProduct();
            }, 5000);
            return;
          } else {
            setError(`error_${response.status}`);
            console.error('Server error details:', errorDetails);
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        
        if (isMounted) {
          if (!data || !data._id) {
            console.error('Invalid product data received:', data);
            setError('invalid_data');
            setLoading(false);
            return;
          }

          // Ensure category is properly handled
          if (!data.category || typeof data.category === 'string') {
            data.category = { name: data.category || 'Uncategorized' };
          }

          if (data.imageUrls && data.imageUrls.length > 0) {
            data.imageUrls = data.imageUrls.map(optimizeImageUrl);
            const img = new Image();
            img.src = data.imageUrls[0];
            img.onload = () => {
              if (isMounted) {
                setImagesLoaded(prev => ({ ...prev, 0: true }));
              }
            };
          }
          
          productCacheRef.current[slug] = {
            data,
            timestamp: Date.now()
          };
          
          setProduct(data);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Product fetch error:', err);
          setError(
            err.name === 'AbortError' 
              ? 'aborted'
              : 'network_error'
          );
          setLoading(false);
        }
      }
    };

    fetchProduct();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.add('in-product-page');
    
    return () => {
      document.body.classList.remove('in-product-page');
    };
  }, []);

  useEffect(() => {
    if (product && productRef.current) {
      gsap.fromTo(galleryRef.current,
        { opacity: 0, x: -50 },
        { opacity: 1, x: 0, duration: 0.6, ease: 'power2.out' }
      );

      gsap.fromTo('.product-summary',
        { opacity: 0, x: 50 },
        { opacity: 1, x: 0, duration: 0.6, ease: 'power2.out', delay: 0.2 }
      );

      gsap.fromTo('.description-section',
        { opacity: 0, y: 30 },
        { 
          opacity: 1, 
          y: 0, 
          duration: 0.4,
          stagger: 0.1,
          ease: 'power2.out',
          delay: 0.4
        }
      );
    }
  }, [product]);

  const handleNextImage = () => {
    if (!product || !product.imageUrls?.length) return;
    
    setCurrentImageIndex((prev) => 
      prev + 1 < product.imageUrls.length ? prev + 1 : 0
    );
  };

  const handlePrevImage = () => {
    if (!product || !product.imageUrls?.length) return;
    
    setCurrentImageIndex((prev) => 
      prev - 1 >= 0 ? prev - 1 : product.imageUrls.length - 1
    );
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      handleNextImage();
    }
    
    if (isRightSwipe) {
      handlePrevImage();
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  const showCartNotification = () => {
    // Create a notification that will be displayed above navbar
    const notification = document.createElement('div');
    notification.className = 'cart-notification';
    
    const content = document.createElement('div');
    content.className = 'notification-content';
    
    // Add checkmark icon
    const icon = document.createElement('span');
    icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1e9c5d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`;
    
    // Add message
    const message = document.createElement('span');
    message.textContent = 'Produkt dodany do koszyka';
    
    // Assemble notification
    content.appendChild(icon);
    content.appendChild(message);
    notification.appendChild(content);
    
    // Add to body
    document.body.appendChild(notification);
    
    // Remove after animation completes
    setTimeout(() => {
      if (notification && notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 2000);
  };

  const handleAddToCart = async () => {
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      if (!token || !userId) {
        alert('Musisz być zalogowany aby dodać produkt do koszyka');
        navigate('/login');
        return;
      }

      if (product.stock <= 0) {
        alert('Przepraszamy, ten produkt jest obecnie niedostępny.');
        return;
      }

      const addingIndicator = document.createElement('div');
      addingIndicator.className = 'adding-to-cart-indicator';
      addingIndicator.textContent = 'Dodawanie do koszyka...';
      document.body.appendChild(addingIndicator);

      // Check if we've recently made a cart request to avoid rate limiting
      const now = Date.now();
      const lastCartRequest = localStorage.getItem('lastCartRequest');
      
      if (lastCartRequest && now - parseInt(lastCartRequest) < 2000) {
        // Wait a bit to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      // Update last request timestamp
      localStorage.setItem('lastCartRequest', now.toString());
      
      console.log('Making cart request to:', `${API_BASE_URL}/api/cart/${userId}/${product._id}`);
      
      const response = await fetch(`${API_BASE_URL}/api/cart/${userId}/${product._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          quantity: 1
        })
      });

      document.body.removeChild(addingIndicator);

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorData;
        
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          const text = await response.text();
          console.error('Non-JSON response:', text);
          throw new Error('Unexpected response from server');
        }
        
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          alert('Sesja wygasła. Zaloguj się ponownie.');
          navigate('/login');
          return;
        }
        
        if (response.status === 429) {
          throw new Error('Zbyt wiele żądań. Spróbuj ponownie za chwilę.');
        }
        
        throw new Error(errorData.message || 'Failed to add item to cart');
      }

      const updatedCart = await response.json();
      showCartNotification();
      window.dispatchEvent(new CustomEvent('cartUpdate', { 
        detail: updatedCart
      }));
      
      const consent = localStorage.getItem('cookie_consent');
      if (consent) {
        const preferences = JSON.parse(consent);
        if (preferences.functional) {
          const recentlyAdded = JSON.parse(localStorage.getItem('recentlyAddedProducts') || '[]');
          if (!recentlyAdded.includes(product._id)) {
            recentlyAdded.unshift(product._id);
            localStorage.setItem('recentlyAddedProducts', JSON.stringify(recentlyAdded.slice(0, 5)));
          }
        }
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(error.message || 'Nie udało się dodać produktu do koszyka. Spróbuj ponownie później.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nie podano';
    
    try {
      const date = new Date(dateString);
      
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('pl-PL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
      
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      return 'Data niedostępna';
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Data niedostępna';
    }
  };

  const renderImageDots = () => {
    if (!product?.imageUrls || product.imageUrls.length <= 1) return null;
    
    return (
      <div className="image-dots">
        {product.imageUrls.map((_, index) => (
          <span 
            key={index} 
            className={`dot ${index === currentImageIndex ? 'active' : ''}`}
            onClick={() => setCurrentImageIndex(index)}
          />
        ))}
      </div>
    );
  };

  // Funkcja renderSections obsługuje teraz wszystkie typy formatowania

  const handleImageLoad = (index) => {
    if (!product?.imageUrls?.[index]) return;
    setImagesLoaded(prev => ({
      ...prev,
      [index]: true
    }));
  };

  const renderImageGallery = () => {
    if (!product?.imageUrls?.length) return null;

    return (
      <div className="image-gallery">
        {product.imageUrls.map((url, index) => (
          <img
            key={`${url}-${index}`}
            src={url}
            alt={`${product.displayName} - widok ${index + 1}`}
            className={`gallery-image ${index === currentImageIndex ? 'active' : ''} ${
              imagesLoaded[index] ? 'loaded' : ''
            }`}
            onLoad={() => handleImageLoad(index)}
            loading={index === 0 ? 'eager' : 'lazy'}
          />
        ))}
        {product.imageUrls.length > 1 && (
          <>
            <button 
              className="gallery-nav prev" 
              onClick={handlePrevImage}
              aria-label="Poprzednie zdjęcie"
            >
              <ChevronLeft />
            </button>
            <button 
              className="gallery-nav next" 
              onClick={handleNextImage}
              aria-label="Następne zdjęcie"
            >
              <ChevronRight />
            </button>
            <div className="image-dots">
              {product.imageUrls.map((_, index) => (
                <span
                  key={index}
                  className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderSections = () => {
    if (!product) return null;

    // Prioritize rendering from the new `sections` array
    if (product.sections && product.sections.length > 0) {
      return product.sections.map((section, index) => {
        const layout = SECTION_LAYOUTS[section.type] || SECTION_LAYOUTS.TXT; // Default to text_only if type is unknown
        const imageUrl = section.imageUrl ? optimizeImageUrl(section.imageUrl) : null; // Use section's own image URL

        return (
          <div key={index} className="description-section">
            <div className={`section-content ${layout}`}>
              {layout === 'text_only' && (
                <div className="section-text">
                  {section.title && <h3>{section.title}</h3>}
                  <p dangerouslySetInnerHTML={{ __html: section.content }}></p>
                </div>
              )}
              {layout === 'image_only' && imageUrl && (
                <div className="section-images">
                  <div className="section-image">
                    <img
                      src={imageUrl}
                      alt={`${product.displayName} - section ${index + 1}`}
                      onError={(e) => e.target.src = '/placeholder.png'}
                      loading="lazy"
                    />
                  </div>
                </div>
              )}
              {(layout === 'image_left' || layout === 'image_right' || layout === 'image_top' || layout === 'text_top') && (
                <>
                  {(layout === 'image_left' || layout === 'image_top') && imageUrl && (
                    <div className="section-images">
                      <div className="section-image">
                        <img
                          src={imageUrl}
                          alt={`${product.displayName} - section ${index + 1}`}
                          onError={(e) => e.target.src = '/placeholder.png'}
                          loading="lazy"
                        />
                      </div>
                    </div>
                  )}
                  <div className="section-text">
                    {section.title && <h3>{section.title}</h3>}
                    <p dangerouslySetInnerHTML={{ __html: section.content }}></p>
                  </div>
                  {(layout === 'image_right' || layout === 'text_top') && imageUrl && (
                    <div className="section-images">
                      <div className="section-image">
                        <img
                          src={imageUrl}
                          alt={`${product.displayName} - section ${index + 1}`}
                          onError={(e) => e.target.src = '/placeholder.png'}
                          loading="lazy"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            {index < product.sections.length - 1 && <hr className="section-divider" />}
          </div>
        );
      });
    }

    // Fallback to old description parsing if no structured sections are available
    if (!product.description) return null;

    const hasLayoutCodes = product.description.includes('---SECTION[');
    
    if (hasLayoutCodes) {
      const sections = product.description.split(/---SECTION\[([A-Z]+)\]---/).filter(Boolean);
      
      return sections.map((section, index) => {
        if (index % 2 === 0) {
          const layoutCode = sections[index - 1];
          const layout = SECTION_LAYOUTS[layoutCode] || SECTION_LAYOUTS.ILE;
          
          const sectionIndex = Math.floor(index / 2);
          const imageUrl = product.imageUrls[sectionIndex] || null;

          // Obsługa starszego formatu HTML w opisie produktu
          const content = section.replace(/<---POGRUBIENIE--->(.*?)<\/---POGRUBIENIE--->/g, '<strong>$1</strong>')
                              .replace(/<---ZIEL--->(.*?)<\/---ZIELONY--->/g, '<span style="color:green">$1</span>')
                              .replace(/<---CZERWONY--->(.*?)<\/---CZERWONY--->/g, '<span style="color:red">$1</span>')
                              .replace(/<---NIEB--->(.*?)<\/---NIEBIESKI--->/g, '<span style="color:blue">$1</span>');

          return (
            <div key={index} className="description-section">
              <div className={`section-content ${layout}`}>
                {layout === 'text_only' && (
                  <div className="section-text">
                    <p dangerouslySetInnerHTML={{ __html: content }}></p>
                  </div>
                )}
                {layout === 'image_only' && imageUrl && (
                  <div className="section-images">
                    <div className="section-image">
                      <img 
                        src={imageUrl} 
                        alt={`${product.displayName} - section ${sectionIndex + 1}`}
                        onError={(e) => e.target.src = '/placeholder.png'}
                        loading={sectionIndex === 0 ? 'eager' : 'lazy'}
                      />
                    </div>
                  </div>
                )}
                {(layout === 'image_left' || layout === 'image_right') && (
                  <>
                    {layout === 'image_left' && imageUrl && (
                      <div className="section-images">
                        <div className="section-image">
                          <img 
                            src={imageUrl} 
                            alt={`${product.displayName} - section ${sectionIndex + 1}`}
                            onError={(e) => e.target.src = '/placeholder.png'}
                            loading={sectionIndex === 0 ? 'eager' : 'lazy'}
                          />
                        </div>
                      </div>
                    )}
                    <div className="section-text">
                      <p dangerouslySetInnerHTML={{ __html: content }}></p>
                    </div>
                    {layout === 'image_right' && imageUrl && (
                      <div className="section-images">
                        <div className="section-image">
                          <img 
                            src={imageUrl} 
                            alt={`${product.displayName} - section ${sectionIndex + 1}`}
                            onError={(e) => e.target.src = '/placeholder.png'}
                            loading={sectionIndex === 0 ? 'eager' : 'lazy'}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
                {(layout === 'image_top' || layout === 'text_top') && (
                  <>
                    {layout === 'image_top' && imageUrl && (
                      <div className="section-images">
                        <div className="section-image">
                          <img 
                            src={imageUrl} 
                            alt={`${product.displayName} - section ${sectionIndex + 1}`}
                            onError={(e) => e.target.src = '/placeholder.png'}
                            loading={sectionIndex === 0 ? 'eager' : 'lazy'}
                          />
                        </div>
                      </div>
                    )}
                    <div className="section-text">
                      <p dangerouslySetInnerHTML={{ __html: content }}></p>
                    </div>
                    {layout === 'text_top' && imageUrl && (
                      <div className="section-images">
                        <div className="section-image">
                          <img 
                            src={imageUrl} 
                            alt={`${product.displayName} - section ${sectionIndex + 1}`}
                            onError={(e) => e.target.src = '/placeholder.png'}
                            loading={sectionIndex === 0 ? 'eager' : 'lazy'}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              {index < sections.length - 2 && <hr className="section-divider" />}
            </div>
          );
        }
        return null;
      }).filter(Boolean);
    } else {
      const sections = product.description.split('---SECTION---').filter(Boolean);
      
      return sections.map((section, index) => {
        const isEven = index % 2 === 0;
        const layout = isEven ? 'image_left' : 'image_right';
        
        const imageUrl = product.imageUrls[index];
        
        // Konwersja starszego formatu do HTML
        const content = section.replace(/<---POGRUBIENIE--->(.*?)<\/---POGRUBIENIE--->/g, '<strong>$1</strong>')
                             .replace(/<---ZIEL--->(.*?)<\/---ZIELONY--->/g, '<span style="color:green">$1</span>')
                             .replace(/<---CZERWONY--->(.*?)<\/---CZERWONY--->/g, '<span style="color:red">$1</span>')
                             .replace(/<---NIEB--->(.*?)<\/---NIEBIESKI--->/g, '<span style="color:blue">$1</span>');

        return (
          <div key={index} className="description-section">
            <div className={`section-content ${layout}`}>
              {layout === 'image_left' ? (
                <>
                  {imageUrl && (
                    <div className="section-images">
                      <div className="section-image">
                        <img 
                          src={imageUrl} 
                          alt={`${product.displayName} - section ${index + 1}`}
                          onError={(e) => e.target.src = '/placeholder.png'}
                          loading={index === 0 ? 'eager' : 'lazy'}
                        />
                      </div>
                    </div>
                  )}
                  <div className="section-text">
                    <p dangerouslySetInnerHTML={{ __html: content }}></p>
                  </div>
                </>
              ) : (
                <>
                  <div className="section-text">
                    <p dangerouslySetInnerHTML={{ __html: content }}></p>
                  </div>
                  {imageUrl && (
                    <div className="section-images">
                      <div className="section-image">
                        <img 
                          src={imageUrl} 
                          alt={`${product.displayName} - section ${index + 1}`}
                          onError={(e) => e.target.src = '/placeholder.png'}
                          loading={index === 0 ? 'eager' : 'lazy'}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            {index < sections.length - 1 && <hr className="section-divider" />}
          </div>
        );
      });
    }
  };

  const renderErrorMessage = () => {
    const errorMessages = {
      not_found: {
        title: 'Produkt nie został znaleziony',
        message: 'Przepraszamy, ale produkt o podanym adresie URL nie istnieje w naszej bazie danych. Mógł zostać usunięty lub przeniesiony.'
      },
      invalid_data: {
        title: 'Nieprawidłowe dane produktu',
        message: 'Otrzymaliśmy nieprawidłowe dane z serwera. Prosimy spróbować później.'
      },
      aborted: {
        title: 'Anulowano ładowanie',
        message: 'Ładowanie produktu zostało anulowane.'
      },
      network_error: {
        title: 'Błąd połączenia',
        message: 'Wystąpił problem z połączeniem z serwerem. Sprawdź swoje połączenie internetowe i spróbuj ponownie.'
      }
    };

    const defaultError = {
      title: 'Wystąpił błąd',
      message: 'Przepraszamy, wystąpił nieoczekiwany błąd. Prosimy spróbować później.'
    };

    const errorInfo = errorMessages[error] || defaultError;

    return (
      <div className="product-page-container">
        <div className="error-container">
          <div className="error-message">
            <h2>{errorInfo.title}</h2>
            <p>{errorInfo.message}</p>
            <div className="error-actions">
              <button 
                className="retry-button" 
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  window.location.reload();
                }}
              >
                Spróbuj ponownie
              </button>
              <button 
                className="back-to-products-button" 
                onClick={() => navigate('/')}
              >
                Wróć do strony głównej
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="product-page-container">
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Ładowanie produktu...</p>
        <p className="loading-subtext">Proszę czekać</p>
      </div>
    </div>
  );
  
  if (error) return renderErrorMessage();
  
  if (!product) return (
    <div className="product-page-container">
      <div className="not-found">Produkt nie został znaleziony</div>
    </div>
  );

  const metaDescription = `${product.displayName} - ${product.description.substring(0, 160).replace(/[<>&'"]/g, '')}...`;
  const canonicalUrl = `https://outletzatorami.pl/product/${product.slug}`;

  return (
    <>
      <Helmet>
        <title>{`${product.displayName} | Outlet Za Torami`}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${product.category?.name || 'Inne'}, outlet, sklep, ${product.displayName}`} />
        <link rel="canonical" href={canonicalUrl} />
        
        <meta property="og:title" content={`${product.displayName} | Outlet Za Torami`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={canonicalUrl} />
        {product.imageUrls && product.imageUrls[0] && (
          <meta property="og:image" content={product.imageUrls[0]} />
        )}
        
        <meta property="product:price:amount" content={product.price} />
        <meta property="product:price:currency" content="PLN" />
        <meta property="product:availability" content={product.stock > 0 ? 'in stock' : 'out of stock'} />
        <meta property="product:condition" content="new" />
        <meta property="product:category" content={product.category?.name || 'Inne'} />
      </Helmet>
      
      <div className="product-page-container" ref={productRef}>
        {notifications.map(id => (
          <div key={id} className="cart-notification">
            <div className="notification-content">
              <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
              <p>Dodano do koszyka!</p>
            </div>
          </div>
        ))}
        <div className="product-page-grid">
          <div className="product-page-header">
            <div 
              className="product-page-gallery" 
              ref={galleryRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {renderImageGallery()}
            </div>
            <div className="product-summary">
              <h1>{product.productPageName || product.displayName}</h1>
              <p className="pprice">{product.price} zł</p>
              <div className="product-details">
                <p className="stock">
                  <Package2 size={18} /> 
                  <span>Na stanie: {product.stock}</span>
                </p>
                <p className="category">
                  <span>Kategoria: {product.category?.name || 'Uncategorized'}</span>
                </p>
                <p className="date-added">
                  <Clock size={18} />
                  <span>Data dodania: {formatDate(product.dateAdded)}</span>
                </p>
              </div>
              <button 
                className="add-to-cart-button" 
                onClick={handleAddToCart}
                disabled={product.stock <= 0}
              >
                <ShoppingCart size={20} />
                {product.stock > 0 ? 'Dodaj do koszyka' : 'Produkt niedostępny'}
              </button>
            </div>
          </div>
          <div className="description">
            <h2>Opis produktu</h2>
            {renderSections()}
          </div>
        </div>
      </div>
    </>
  );
}
